/**
 * NotificationAdmin - Admin page for notification system management
 *
 * Based on communication_protocol_spec-2.md sections 2.1 and 2.2
 * Features:
 * - View/edit notification templates
 * - Enable/disable notification types
 * - Manage user groups (distribution lists)
 * - Add/remove group members
 */

import { useState, useEffect } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || '';

// Tab options
const TABS = [
  { id: 'templates', label: 'Notification Templates' },
  { id: 'groups', label: 'User Groups' },
];

export default function NotificationAdmin({ token }) {
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [templatesRes, groupsRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/notifications/templates`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
        fetch(`${API_BASE}/api/admin/notifications/groups`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
      ]);

      if (templatesRes.ok) setTemplates(await templatesRes.json());
      if (groupsRes.ok) setGroups(await groupsRes.json());
    } catch (err) {
      console.error('Error fetching notification admin data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTemplate = async (type) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/notifications/templates/${type}/toggle`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const updated = await res.json();
        setTemplates(templates.map(t => t.notification_type === type ? updated : t));
      }
    } catch (err) {
      console.error('Error toggling template:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 bg-slate-200 text-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading notification settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-slate-200 text-gray-900 overflow-auto">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Notification System</h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage notification templates and user groups
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-gray-200">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'text-gray-900 border-purple-500'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <TemplatesTab
            templates={templates}
            onToggle={handleToggleTemplate}
            onEdit={setEditingTemplate}
          />
        )}

        {/* Groups Tab */}
        {activeTab === 'groups' && (
          <GroupsTab
            groups={groups}
            onEdit={setEditingGroup}
            onNewGroup={() => setShowNewGroupModal(true)}
            onRefresh={fetchData}
            token={token}
          />
        )}
      </div>

      {/* Edit Template Modal */}
      {editingTemplate && (
        <EditTemplateModal
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSave={(updated) => {
            setTemplates(templates.map(t => t.notification_type === updated.notification_type ? updated : t));
            setEditingTemplate(null);
          }}
          token={token}
        />
      )}

      {/* Edit Group Modal */}
      {editingGroup && (
        <EditGroupModal
          group={editingGroup}
          onClose={() => setEditingGroup(null)}
          onRefresh={fetchData}
          token={token}
        />
      )}

      {/* New Group Modal */}
      {showNewGroupModal && (
        <NewGroupModal
          onClose={() => setShowNewGroupModal(false)}
          onCreated={() => {
            setShowNewGroupModal(false);
            fetchData();
          }}
          token={token}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   TEMPLATES TAB
───────────────────────────────────────────────────────────────────────────── */

function TemplatesTab({ templates, onToggle, onEdit }) {
  return (
    <div className="space-y-3">
      {templates.map((template) => (
        <div
          key={template.notification_type}
          className="bg-white border border-gray-200 rounded-lg p-4"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h3 className="text-gray-900 font-medium">{template.display_name}</h3>
                <span
                  className={`px-2 py-0.5 text-xs rounded ${
                    template.is_active
                      ? 'bg-green-100 text-green-400 border border-green-800'
                      : 'bg-slate-100 text-gray-500 border border-gray-200'
                  }`}
                >
                  {template.is_active ? 'Active' : 'Disabled'}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                <span className="text-gray-500">ID:</span> {template.notification_type}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                <span className="text-gray-500">Subject:</span> {template.subject_format}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                <span className="text-gray-500">Triggered by:</span>{' '}
                <span className="capitalize">{template.triggered_by_role || 'Any'}</span>
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {template.primary_recipients?.map((r) => (
                  <span
                    key={r}
                    className="px-2 py-0.5 text-xs bg-purple-900/30 text-purple-600 rounded border border-purple-800/50"
                  >
                    {r}
                  </span>
                ))}
                {template.cc_recipients?.map((r) => (
                  <span
                    key={r}
                    className="px-2 py-0.5 text-xs bg-slate-100 text-gray-500 rounded border border-gray-200"
                  >
                    cc: {r}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0 ml-4">
              <button
                onClick={() => onToggle(template.notification_type)}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  template.is_active
                    ? 'bg-slate-100 hover:bg-slate-100 text-gray-700'
                    : 'bg-green-100 hover:bg-green-800/50 text-green-600'
                }`}
              >
                {template.is_active ? 'Disable' : 'Enable'}
              </button>
              <button
                onClick={() => onEdit(template)}
                className="px-3 py-1.5 text-xs font-medium bg-purple-100 hover:bg-purple-800/50 text-purple-600 rounded transition-colors"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   GROUPS TAB
───────────────────────────────────────────────────────────────────────────── */

function GroupsTab({ groups, onEdit, onNewGroup, onRefresh, token }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-500">
          {groups.length} distribution group{groups.length !== 1 ? 's' : ''}
        </p>
        <button
          onClick={onNewGroup}
          className="px-3 py-1.5 text-sm font-medium bg-purple-600 hover:bg-purple-700 text-gray-900 rounded transition-colors"
        >
          + New Group
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {groups.map((group) => (
          <div
            key={group.group_id}
            className="bg-white border border-gray-200 rounded-lg p-4"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-gray-900 font-medium">{group.display_name}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{group.group_id}</p>
                {group.description && (
                  <p className="text-sm text-gray-500 mt-2">{group.description}</p>
                )}
                <p className="text-sm text-gray-500 mt-2">
                  <span className="text-blue-400 font-medium">{group.member_count}</span> member
                  {group.member_count !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => onEdit(group)}
                className="px-3 py-1.5 text-xs font-medium bg-slate-100 hover:bg-slate-100 text-gray-700 rounded transition-colors"
              >
                Manage
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   EDIT TEMPLATE MODAL
───────────────────────────────────────────────────────────────────────────── */

function EditTemplateModal({ template, onClose, onSave, token }) {
  const [form, setForm] = useState({
    display_name: template.display_name || '',
    subject_format: template.subject_format || '',
    default_body_template: template.default_body_template || '',
    triggered_by_role: template.triggered_by_role || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/notifications/templates/${template.notification_type}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(form),
        }
      );
      if (res.ok) {
        const updated = await res.json();
        onSave(updated);
      }
    } catch (err) {
      console.error('Error saving template:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white border border-gray-200 rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Edit Template</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
            <input
              type="text"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              className="w-full px-3 py-2 bg-slate-100 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject Format</label>
            <input
              type="text"
              value={form.subject_format}
              onChange={(e) => setForm({ ...form, subject_format: e.target.value })}
              className="w-full px-3 py-2 bg-slate-100 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Use {'{{variable}}'} for placeholders
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Body Template</label>
            <textarea
              value={form.default_body_template}
              onChange={(e) => setForm({ ...form, default_body_template: e.target.value })}
              rows={6}
              className="w-full px-3 py-2 bg-slate-100 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-purple-500 resize-none font-mono"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Triggered By Role</label>
            <select
              value={form.triggered_by_role}
              onChange={(e) => setForm({ ...form, triggered_by_role: e.target.value })}
              className="w-full px-3 py-2 bg-slate-100 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-purple-500"
            >
              <option value="">Any</option>
              <option value="pa">Production Art</option>
              <option value="pm">Project Manager</option>
              <option value="assembly">Assembly</option>
              <option value="shipping">Shipping</option>
              <option value="sales">Sales</option>
            </select>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-100 text-gray-700 rounded-lg transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 text-gray-900 rounded-lg transition-colors text-sm font-medium"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   EDIT GROUP MODAL
───────────────────────────────────────────────────────────────────────────── */

function EditGroupModal({ group, onClose, onRefresh, token }) {
  const [members, setMembers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingUser, setAddingUser] = useState(null);

  useEffect(() => {
    fetchGroupDetails();
  }, [group.group_id]);

  const fetchGroupDetails = async () => {
    setLoading(true);
    try {
      const [groupRes, usersRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/notifications/groups/${group.group_id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
        fetch(`${API_BASE}/api/admin/notifications/available-users`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }),
      ]);

      if (groupRes.ok) {
        const data = await groupRes.json();
        setMembers(data.members || []);
      }
      if (usersRes.ok) {
        setAvailableUsers(await usersRes.json());
      }
    } catch (err) {
      console.error('Error fetching group details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (user) => {
    setAddingUser(user.user_id);
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/notifications/groups/${group.group_id}/members`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(user),
        }
      );
      if (res.ok) {
        const newMember = await res.json();
        setMembers([...members, newMember]);
      }
    } catch (err) {
      console.error('Error adding member:', err);
    } finally {
      setAddingUser(null);
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/notifications/groups/${group.group_id}/members/${userId}`,
        {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      if (res.ok) {
        setMembers(members.filter((m) => m.user_id !== userId));
      }
    } catch (err) {
      console.error('Error removing member:', err);
    }
  };

  const memberIds = new Set(members.map((m) => m.user_id));
  const nonMembers = availableUsers.filter((u) => !memberIds.has(u.user_id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white border border-gray-200 rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{group.display_name}</h2>
            <p className="text-xs text-gray-500">{group.group_id}</p>
          </div>
          <button
            onClick={() => { onClose(); onRefresh(); }}
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500 mx-auto" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6">
              {/* Current Members */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Current Members ({members.length})
                </h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {members.length === 0 ? (
                    <p className="text-sm text-gray-500">No members yet</p>
                  ) : (
                    members.map((member) => (
                      <div
                        key={member.user_id}
                        className="flex items-center justify-between px-3 py-2 bg-slate-100 rounded-lg"
                      >
                        <div>
                          <div className="text-sm text-gray-900">{member.user_name}</div>
                          {member.user_email && (
                            <div className="text-xs text-gray-500">{member.user_email}</div>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemoveMember(member.user_id)}
                          className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                          title="Remove from group"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Available Users */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Available Users ({nonMembers.length})
                </h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {nonMembers.length === 0 ? (
                    <p className="text-sm text-gray-500">All users are in this group</p>
                  ) : (
                    nonMembers.map((user) => (
                      <div
                        key={user.user_id}
                        className="flex items-center justify-between px-3 py-2 bg-slate-100 rounded-lg"
                      >
                        <div>
                          <div className="text-sm text-gray-900">{user.user_name}</div>
                          {user.user_email && (
                            <div className="text-xs text-gray-500">{user.user_email}</div>
                          )}
                        </div>
                        <button
                          onClick={() => handleAddMember(user)}
                          disabled={addingUser === user.user_id}
                          className="px-2 py-1 text-xs font-medium bg-purple-100 hover:bg-purple-800/50 disabled:bg-gray-700 text-purple-600 rounded transition-colors"
                        >
                          {addingUser === user.user_id ? '...' : 'Add'}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   NEW GROUP MODAL
───────────────────────────────────────────────────────────────────────────── */

function NewGroupModal({ onClose, onCreated, token }) {
  const [form, setForm] = useState({
    group_id: '',
    display_name: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!form.group_id || !form.display_name) {
      setError('Group ID and Display Name are required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/notifications/groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(form),
      });

      if (res.ok) {
        onCreated();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to create group');
      }
    } catch (err) {
      console.error('Error creating group:', err);
      setError('Failed to create group');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white border border-gray-200 rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">New User Group</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-900/30 border border-red-800 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Group ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.group_id}
              onChange={(e) => setForm({ ...form, group_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
              placeholder="e.g., qa_team"
              className="w-full px-3 py-2 bg-slate-100 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers, underscores only</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              placeholder="e.g., QA Team"
              className="w-full px-3 py-2 bg-slate-100 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Who is in this group..."
              rows={2}
              className="w-full px-3 py-2 bg-slate-100 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-purple-500 resize-none"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-100 text-gray-700 rounded-lg transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !form.group_id || !form.display_name}
            className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:text-gray-500 text-gray-900 rounded-lg transition-colors text-sm font-medium"
          >
            {saving ? 'Creating...' : 'Create Group'}
          </button>
        </div>
      </div>
    </div>
  );
}
